import { useEffect, useState } from 'react';
import { ContentImageField } from '@/components/admin/content-image-field';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Trash2, ArrowLeft, Eye, GripVertical, X, Tag, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { normalizeImageUrl } from '@/lib/utils';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';
import { useAdminPreview } from '@/lib/admin-preview-context';
import { Database } from '@/types/supabase';
import { RichTextViewer } from '@/components/ui/rich-text-viewer';
import { AudiencePicker } from '@/components/admin/audience-picker';
import Loading from '@/components/loading';
import { useToast } from '@/components/ui/use-toast';
import { useNewsTopics, NewsTopic } from '@/lib/hooks/use-news-topics';
import { NewsKeywordManager } from '@/components/admin/news-keyword-manager';
import { useKeywords } from '@/lib/hooks/use-keywords';
import { Keyword } from '@/types/custom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type NewsItem = Database['public']['Tables']['news_items']['Row'];
type ScopeType = 'global' | 'sector' | 'organization' | 'team' | 'user' | 'hoofdhartuser' | 'samen';

interface ScopeEntity {
  id: string;
  name: string;
}

const SCOPE_LABELS: Record<ScopeType, string> = {
  global: 'Iedereen',
  sector: 'Sector',
  organization: 'Organisatie',
  team: 'Team',
  user: 'Gebruiker',
  hoofdhartuser: 'HnH Gebruikers',
  samen: 'Samen',
};

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '...';
}

function SortableNewsCard({
  item,
  isAdmin,
  onOpen,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  item: NewsItem;
  isAdmin: boolean;
  onOpen: (id: string) => void;
  onEdit: (item: NewsItem) => void;
  onDuplicate: (item: NewsItem) => void;
  onDelete: (item: NewsItem) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const preview = item.body ? truncateText(stripHtml(item.body), 120) : '';

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className="overflow-hidden flex flex-col cursor-pointer hover:shadow-lg transition-shadow h-full"
        onClick={() => onOpen(item.id)}
      >
        {item.image_url && (
          <img
            src={normalizeImageUrl(item.image_url)}
            alt={item.title}
            className="w-full h-48 object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <div className="p-5 flex flex-col flex-grow">
          <div className="flex items-start justify-between gap-2">
            {isAdmin && (
              <button
                className="p-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0 touch-none"
                {...attributes}
                {...listeners}
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
            <h2 className="text-lg font-semibold leading-tight flex-grow">
              {item.title}
            </h2>
            {isAdmin && (
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDuplicate(item); }}
                  className="p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-950"
                >
                  <Copy className="h-4 w-4 text-muted-foreground" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(item); }}
                  className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            )}
          </div>

          {!item.published && isAdmin && (
            <span className="mt-1 text-xs text-amber-600 font-medium">
              (concept)
            </span>
          )}

          {preview && (
            <p className="mt-3 text-sm text-muted-foreground line-clamp-3">
              {preview}
            </p>
          )}

          <span className="mt-3 text-sm font-medium text-green-600">
            Lees meer
          </span>
        </div>
      </Card>
    </div>
  );
}

export default function NieuwsPage() {
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;
  const { isPreviewMode } = useAdminPreview();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // View As state (admin only)
  const [scopeType, setScopeType] = useState<ScopeType>('global');
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [sectors, setSectors] = useState<ScopeEntity[]>([]);
  const [organizations, setOrganizations] = useState<ScopeEntity[]>([]);
  const [teams, setTeams] = useState<ScopeEntity[]>([]);
  const [users, setUsers] = useState<ScopeEntity[]>([]);
  const [samenGroups, setSamenGroups] = useState<ScopeEntity[]>([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NewsItem | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [published, setPublished] = useState(true);
  const [dialogScopeType, setDialogScopeType] = useState<string>('global');
  const [dialogScopeId, setDialogScopeId] = useState<string | null>(null);
  const [dialogRoleScope, setDialogRoleScope] = useState<string | null>(null);
  const [dialogHnhScopeType, setDialogHnhScopeType] = useState<string>('global');
  const [dialogHnhScopeId, setDialogHnhScopeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dialogTopicIds, setDialogTopicIds] = useState<string[]>([]);
  const [inlineNewTopicName, setInlineNewTopicName] = useState('');

  const { toast } = useToast();

  // News topics state
  const { topics: newsTopics, loading: loadingTopics, createTopic, updateTopic, deleteTopic, getTopicsForNewsItem, setTopicsForNewsItem } = useNewsTopics();
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<NewsTopic | null>(null);
  const [topicName, setTopicName] = useState('');
  const [topicSlug, setTopicSlug] = useState('');

  // Per-item topic associations (news_item_id -> topic_id[])
  const [itemTopics, setItemTopics] = useState<Record<string, string[]>>({});
  // Topics for the currently selected news item (detail view)
  const [selectedItemTopicIds, setSelectedItemTopicIds] = useState<string[]>([]);
  // Keywords for the currently selected news item
  const { getKeywordsForNewsItem } = useKeywords();
  const [selectedItemKeywords, setSelectedItemKeywords] = useState<Keyword[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Fetch entity lists for admin View As
  useEffect(() => {
    if (!isAdmin) return;
    const fetchEntities = async () => {
      const [sectorRes, orgRes, teamRes, userRes, samenRes] = await Promise.all([
        supabase.from('sector').select('id, name').order('name'),
        supabase.from('organization').select('id, name').order('name'),
        supabase.from('team').select('id, name').order('name'),
        supabase.from('profiles').select('id, naam').order('naam'),
        supabase.from('samen').select('id, name').order('name'),
      ]);
      if (sectorRes.data) setSectors(sectorRes.data.map((s) => ({ id: s.id, name: s.name })));
      if (orgRes.data) setOrganizations(orgRes.data.map((o) => ({ id: o.id, name: o.name })));
      if (teamRes.data) setTeams(teamRes.data.map((t) => ({ id: t.id, name: t.name })));
      if (userRes.data)
        setUsers(
          userRes.data
            .filter((u) => u.naam)
            .map((u) => ({ id: u.id, name: u.naam! })),
        );
      if (samenRes.data) setSamenGroups(samenRes.data.map((s) => ({ id: s.id, name: s.name })));
    };
    fetchEntities();
  }, [isAdmin]);

  // Reset scopeId when scopeType changes
  useEffect(() => {
    setScopeId(null);
  }, [scopeType]);

  // Reset scope when preview mode changes
  useEffect(() => {
    setScopeType('global');
    setScopeId(null);
  }, [isPreviewMode]);

  const getEntityList = (): ScopeEntity[] => {
    switch (scopeType) {
      case 'sector': return sectors;
      case 'organization': return organizations;
      case 'team': return teams;
      case 'user': return users;
      case 'samen': return samenGroups;
      default: return [];
    }
  };

  // Fetch news items based on scope (admin) or user's scopes (regular user)
  const fetchNews = async () => {
    setLoading(true);

    if (isAdmin) {
      if (scopeType !== 'global' && !scopeId) {
        setNewsItems([]);
        setLoading(false);
        return;
      }

      // Build scopes that the selected entity would see
      const scopes: string[] = [
        'and(scope_type.eq.global,scope_id.is.null)',
      ];

      if (scopeType === 'user' && scopeId) {
        scopes.push(`and(scope_type.eq.user,scope_id.eq.${scopeId})`);
        // Also find the user's teams to include team/org/sector scopes
        const { data: memberships } = await supabase
          .from('team_members')
          .select('team:team(id, organization_id, organization:organization(id, sector_id))')
          .eq('profile_id', scopeId);
        for (const m of memberships ?? []) {
          const t = m.team as any;
          if (t) {
            scopes.push(`and(scope_type.eq.team,scope_id.eq.${t.id})`);
            scopes.push(`and(scope_type.eq.organization,scope_id.eq.${t.organization_id})`);
            if (t.organization?.sector_id) {
              scopes.push(`and(scope_type.eq.sector,scope_id.eq.${t.organization.sector_id})`);
            }
          }
        }
      } else if (scopeType === 'team' && scopeId) {
        scopes.push(`and(scope_type.eq.team,scope_id.eq.${scopeId})`);
        // Look up org and sector for this team
        const { data: teamData } = await supabase
          .from('team')
          .select('organization_id, organization:organization(sector_id)')
          .eq('id', scopeId)
          .single();
        if (teamData) {
          scopes.push(`and(scope_type.eq.organization,scope_id.eq.${teamData.organization_id})`);
          const org = teamData.organization as any;
          if (org?.sector_id) {
            scopes.push(`and(scope_type.eq.sector,scope_id.eq.${org.sector_id})`);
          }
        }
      } else if (scopeType === 'organization' && scopeId) {
        scopes.push(`and(scope_type.eq.organization,scope_id.eq.${scopeId})`);
        const { data: orgData } = await supabase
          .from('organization')
          .select('sector_id')
          .eq('id', scopeId)
          .single();
        if (orgData?.sector_id) {
          scopes.push(`and(scope_type.eq.sector,scope_id.eq.${orgData.sector_id})`);
        }
      } else if (scopeType === 'sector' && scopeId) {
        scopes.push(`and(scope_type.eq.sector,scope_id.eq.${scopeId})`);
      }

      const { data } = await supabase
        .from('news_items')
        .select('*')
        .or(scopes.join(','))
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (data) setNewsItems(data);
    } else {
      const scopes: string[] = [
        'and(scope_type.eq.global,scope_id.is.null)',
        `and(scope_type.eq.user,scope_id.eq.${profile.id})`,
      ];

      for (const team of profile?.teams ?? []) {
        scopes.push(`and(scope_type.eq.team,scope_id.eq.${team.id})`);
        scopes.push(`and(scope_type.eq.organization,scope_id.eq.${team.organization.id})`);
        scopes.push(`and(scope_type.eq.sector,scope_id.eq.${team.organization.sector.id})`);
      }

      const { data } = await supabase
        .from('news_items')
        .select('*')
        .or(scopes.join(','))
        .eq('published', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (data) setNewsItems(data);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!loadingProfile && profile) fetchNews();
  }, [scopeType, scopeId, loadingProfile, isAdmin]);

  // Fetch topic associations for all loaded news items
  useEffect(() => {
    if (newsItems.length === 0) return;
    const fetchAssociations = async () => {
      const map: Record<string, string[]> = {};
      await Promise.all(
        newsItems.map(async (item) => {
          map[item.id] = await getTopicsForNewsItem(item.id);
        }),
      );
      setItemTopics(map);
    };
    fetchAssociations();
  }, [newsItems]);

  const selectedItem = selectedId
    ? newsItems.find((n) => n.id === selectedId)
    : null;

  const openItem = async (id: string) => {
    setSelectedId(id);
    const topicIds = await getTopicsForNewsItem(id);
    setSelectedItemTopicIds(topicIds);
    const kws = await getKeywordsForNewsItem(id);
    setSelectedItemKeywords(kws);
  };
  const closeItem = () => { setSelectedId(null); setSelectedItemKeywords([]); };

  const openCreate = () => {
    setEditing(null);
    setTitle('');
    setBody('');
    setImageUrl('');
    setPublished(true);
    setDialogScopeType('global');
    setDialogScopeId(null);
    setDialogRoleScope(null);
    setDialogHnhScopeType('global');
    setDialogHnhScopeId(null);
    setDialogTopicIds([]);
    setInlineNewTopicName('');
    setDialogOpen(true);
  };

  const openEdit = async (item: NewsItem) => {
    setEditing(item);
    setTitle(item.title);
    setBody(item.body ?? '');
    setImageUrl(item.image_url ?? '');
    setPublished(item.published);
    setDialogScopeType(item.scope_type ?? 'global');
    setDialogScopeId(item.scope_id ?? null);
    setDialogRoleScope(item.role_scope ?? null);
    setDialogHnhScopeType(item.hnh_scope_type ?? 'global');
    setDialogHnhScopeId(item.hnh_scope_id ?? null);
    setInlineNewTopicName('');
    const existingTopicIds = await getTopicsForNewsItem(item.id);
    setDialogTopicIds(existingTopicIds);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSubmitting(true);

    try {
      const saveScopeId = dialogScopeType === 'global' ? null : dialogScopeId;
      const saveHnhScopeId = dialogHnhScopeType === 'global' || dialogHnhScopeType === 'hoofdhartuser' ? null : dialogHnhScopeId;
      let savedItemId: string;

      if (editing) {
        const { error } = await supabase
          .from('news_items')
          .update({
            title: title.trim(),
            body: body.trim() || null,
            image_url: imageUrl.trim() || null,
            published,
            role_scope: dialogRoleScope,
            scope_type: dialogScopeType,
            scope_id: saveScopeId,
            hnh_scope_type: dialogHnhScopeType,
            hnh_scope_id: saveHnhScopeId,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editing.id);
        if (error) throw error;
        savedItemId = editing.id;
      } else {
        const { data, error } = await supabase.from('news_items').insert({
          title: title.trim(),
          body: body.trim() || null,
          image_url: imageUrl.trim() || null,
          published,
          role_scope: dialogRoleScope,
          scope_type: dialogScopeType,
          scope_id: saveScopeId,
          hnh_scope_type: dialogHnhScopeType,
          hnh_scope_id: saveHnhScopeId,
          created_by: profile.id,
        }).select('id').single();
        if (error) throw error;
        savedItemId = data.id;
      }

      // Save topic associations
      await setTopicsForNewsItem(savedItemId, dialogTopicIds);
      setItemTopics((prev) => ({ ...prev, [savedItemId]: dialogTopicIds }));

      setDialogOpen(false);
      fetchNews();
    } catch (err) {
      console.error('Error saving news item:', err);
      alert('Er ging iets mis bij het opslaan.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item: NewsItem) => {
    if (!confirm('Weet je zeker dat je dit nieuwsbericht wilt verwijderen?'))
      return;
    await supabase.from('news_items').delete().eq('id', item.id);
    if (selectedId === item.id) setSelectedId(null);
    fetchNews();
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = newsItems.findIndex((i) => i.id === active.id);
    const newIndex = newsItems.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(newsItems, oldIndex, newIndex);
    setNewsItems(reordered);

    // Persist new order
    const updates = reordered.map((item, idx) => ({
      id: item.id,
      sort_order: idx,
    }));
    for (const u of updates) {
      await supabase.from('news_items').update({ sort_order: u.sort_order }).eq('id', u.id);
    }
  };

  const handleDuplicate = async (item: NewsItem) => {
    try {
      const topicIds = await getTopicsForNewsItem(item.id);
      const { data, error } = await supabase.from('news_items').insert({
        title: `${item.title} (kopie)`,
        body: item.body,
        image_url: item.image_url,
        published: false,
        role_scope: item.role_scope,
        scope_type: item.scope_type,
        scope_id: item.scope_id,
        hnh_scope_type: item.hnh_scope_type,
        hnh_scope_id: item.hnh_scope_id,
        created_by: profile.id,
      }).select('id').single();
      if (error) throw error;
      if (topicIds.length > 0) {
        await setTopicsForNewsItem(data.id, topicIds);
      }
      toast({ title: 'Nieuwsbericht gedupliceerd', description: `'${item.title}' is gekopieerd.` });
      fetchNews();
    } catch (err: any) {
      toast({ title: 'Fout bij dupliceren', description: err.message, variant: 'destructive' });
    }
  };

  const renderAdminButtons = (item: NewsItem) => {
    if (!isAdmin) return null;
    return (
      <div className="flex gap-1 flex-shrink-0">
        <button
          onClick={() => openEdit(item)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </button>
        <button
          onClick={() => handleDuplicate(item)}
          className="p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-950"
        >
          <Copy className="h-4 w-4 text-muted-foreground" />
        </button>
        <button
          onClick={() => handleDelete(item)}
          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950"
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </button>
      </div>
    );
  };

  // Filter news items by active topic
  const filteredItems = activeTopicId
    ? newsItems.filter((item) =>
        (itemTopics[item.id] ?? []).includes(activeTopicId),
      )
    : newsItems;

  if (loadingProfile || loading) return <Loading />;

  // Detail view
  if (selectedItem) {
    return (
      <div className="min-h-screen p-8 space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={closeItem}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-semibold">Nieuws</h1>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="overflow-hidden">
            {selectedItem.image_url && (
              <img
                src={normalizeImageUrl(selectedItem.image_url)}
                alt={selectedItem.title}
                className="w-full h-64 sm:h-80 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
                  {selectedItem.title}
                </h2>
                {renderAdminButtons(selectedItem)}
              </div>

              {selectedItem.body && (
                <div className="mt-6">
                  <RichTextViewer
                    content={selectedItem.body}
                    className="text-base"
                  />
                </div>
              )}

              {/* Keywords display */}
              {selectedItemKeywords.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Keywords</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedItemKeywords.map((kw) => (
                      <span key={kw.id} className="inline-flex items-center rounded-full border border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 px-3 py-1 text-xs hhu-outline">
                        {kw.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Topics display / admin assignment */}
              {(selectedItemTopicIds.length > 0 || isAdmin) && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Topics</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {newsTopics.map((topic) => {
                      const isAssigned = selectedItemTopicIds.includes(topic.id);
                      if (!isAdmin && !isAssigned) return null;
                      return (
                        <Button
                          key={topic.id}
                          variant="outline"
                          size="sm"
                          className={`rounded-full text-xs ${
                            isAssigned
                              ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:hover:bg-blue-900'
                              : 'border-muted text-muted-foreground hover:bg-muted/80'
                          } ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
                          onClick={async () => {
                            if (!isAdmin) return;
                            const newIds = isAssigned
                              ? selectedItemTopicIds.filter((id) => id !== topic.id)
                              : [...selectedItemTopicIds, topic.id];
                            setSelectedItemTopicIds(newIds);
                            try {
                              await setTopicsForNewsItem(selectedItem.id, newIds);
                              setItemTopics((prev) => ({ ...prev, [selectedItem.id]: newIds }));
                            } catch (err: any) {
                              toast({ title: 'Fout', description: err.message, variant: 'destructive' });
                              setSelectedItemTopicIds(isAssigned
                                ? [...selectedItemTopicIds]
                                : selectedItemTopicIds.filter((id) => id !== topic.id));
                            }
                          }}
                        >
                          {topic.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Edit dialog in detail view */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Nieuwsbericht bewerken</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="news-title">Titel</Label>
                <Input id="news-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel van het bericht" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="news-body">Inhoud</Label>
                <Textarea id="news-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Schrijf hier het bericht..." rows={6} />
              </div>
              <ContentImageField
                id="news-image"
                value={imageUrl}
                onChange={setImageUrl}
                label="Afbeelding"
              />
              <AudiencePicker
                roleScope={dialogRoleScope}
                scopeType={dialogScopeType}
                scopeId={dialogScopeId}
                hnhScopeType={dialogHnhScopeType}
                hnhScopeId={dialogHnhScopeId}
                onRoleScopeChange={setDialogRoleScope}
                onScopeChange={(type, id) => {
                  setDialogScopeType(type);
                  setDialogScopeId(id);
                }}
                onHnhScopeChange={(type, id) => {
                  setDialogHnhScopeType(type);
                  setDialogHnhScopeId(id);
                }}
              />
              {/* Topic selection */}
              <div className="grid gap-2">
                <Label>Topics</Label>
                <div className="flex flex-wrap gap-2">
                  {newsTopics.map((topic) => {
                    const isSelected = dialogTopicIds.includes(topic.id);
                    return (
                      <Button
                        key={topic.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`rounded-full text-xs ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300'
                            : 'border-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        onClick={() => {
                          setDialogTopicIds(
                            isSelected
                              ? dialogTopicIds.filter((id) => id !== topic.id)
                              : [...dialogTopicIds, topic.id],
                          );
                        }}
                      >
                        {topic.name}
                      </Button>
                    );
                  })}
                </div>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={inlineNewTopicName}
                    onChange={(e) => setInlineNewTopicName(e.target.value)}
                    placeholder="Nieuw topic..."
                    className="h-8 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!inlineNewTopicName.trim()) return;
                        const slug = inlineNewTopicName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                        createTopic(inlineNewTopicName.trim(), slug)
                          .then(() => { setInlineNewTopicName(''); toast({ title: 'Topic aangemaakt' }); })
                          .catch((err) => toast({ title: 'Fout', description: err.message, variant: 'destructive' }));
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 border-blue-400 text-blue-600 hover:bg-blue-50"
                    disabled={!inlineNewTopicName.trim()}
                    onClick={() => {
                      if (!inlineNewTopicName.trim()) return;
                      const slug = inlineNewTopicName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                      createTopic(inlineNewTopicName.trim(), slug)
                        .then(() => { setInlineNewTopicName(''); toast({ title: 'Topic aangemaakt' }); })
                        .catch((err) => toast({ title: 'Fout', description: err.message, variant: 'destructive' }));
                    }}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              {editing && (
                <NewsKeywordManager newsItemId={editing.id} />
              )}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="news-published" checked={published} onChange={(e) => setPublished(e.target.checked)} className="rounded border-gray-300" />
                <Label htmlFor="news-published">Gepubliceerd</Label>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleSave} disabled={submitting || !title.trim()} className="bg-green-600 hover:bg-green-700 text-white">
                {submitting ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main view
  return (
    <div className="min-h-screen p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Nieuws</h1>
        {isAdmin && (
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={openCreate}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nieuw bericht
          </Button>
        )}
      </div>

      {/* View As section (admin only) */}
      {isAdmin && (
        <section className="bg-card border rounded-lg px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 mr-2">
              <Eye className="h-4 w-4 text-green-600" />
              <span className="text-sm font-semibold">Bekijken als</span>
            </div>
            <div className="w-44">
              <Select
                value={scopeType}
                onValueChange={(val) => setScopeType(val as ScopeType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isPreviewMode ? (
                    <>
                      <SelectItem value="global">Iedereen</SelectItem>
                      <SelectItem value="samen">Samen</SelectItem>
                      <SelectItem value="hoofdhartuser">HnH Gebruikers</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="global">Iedereen</SelectItem>
                      <SelectItem value="sector">Sector</SelectItem>
                      <SelectItem value="organization">Organisatie</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                      <SelectItem value="user">Gebruiker</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {scopeType !== 'global' && scopeType !== 'hoofdhartuser' && (
              <div className="w-56">
                <Select
                  value={scopeId ?? ''}
                  onValueChange={(val) => setScopeId(val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`Selecteer ${SCOPE_LABELS[scopeType].toLowerCase()}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {getEntityList().map((entity) => (
                      <SelectItem key={entity.id} value={entity.id}>
                        {entity.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Topic filter buttons */}
      {!loadingTopics && newsTopics.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 cap-scroll-row">
          {newsTopics.map((topic) => (
            <div key={topic.id} className="relative group">
              <Button
                variant="outline"
                className={`rounded-full flex-shrink-0 ${
                  activeTopicId === topic.id
                    ? 'border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300'
                    : 'border-blue-400 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950'
                }`}
                onClick={() => {
                  setActiveTopicId(activeTopicId === topic.id ? null : topic.id);
                }}
              >
                {topic.name}
              </Button>
              {isAdmin && (
                <div className="absolute -top-1 -right-1 hidden group-hover:flex gap-0.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTopic(topic);
                      setTopicName(topic.name);
                      setTopicSlug(topic.slug);
                      setTopicDialogOpen(true);
                    }}
                    className="flex h-5 w-5 items-center justify-center rounded-full bg-white shadow text-blue-600 hover:bg-blue-50"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`'${topic.name}' verwijderen?`)) {
                        deleteTopic(topic.id).then(() =>
                          toast({ title: 'Topic verwijderd', description: `'${topic.name}' is verwijderd.` }),
                        ).catch((err) =>
                          toast({ title: 'Fout', description: err.message, variant: 'destructive' }),
                        );
                      }
                    }}
                    className="flex h-5 w-5 items-center justify-center rounded-full bg-white shadow text-red-500 hover:bg-red-50"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-dashed border-blue-400 text-blue-600 hover:bg-blue-50"
              onClick={() => {
                setEditingTopic(null);
                setTopicName('');
                setTopicSlug('');
                setTopicDialogOpen(true);
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Topic
            </Button>
          )}
        </div>
      )}

      {filteredItems.length === 0 && (
        <p className="text-muted-foreground">
          {isAdmin && scopeType !== 'global' && !scopeId
            ? `Selecteer een ${SCOPE_LABELS[scopeType].toLowerCase()} om nieuws te bekijken.`
            : activeTopicId
              ? 'Geen nieuwsberichten gevonden voor dit topic.'
              : 'Er zijn momenteel geen nieuwsberichten.'}
        </p>
      )}

      {/* Featured first item */}
      {filteredItems.length > 0 && (() => {
        const firstItem = filteredItems[0];
        return (
          <div className="max-w-6xl">
            <Card className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openItem(firstItem.id)}>
              <div className="grid grid-cols-1 md:grid-cols-2">
                {firstItem.image_url && (
                  <img
                    src={normalizeImageUrl(firstItem.image_url)}
                    alt={firstItem.title}
                    className="w-full h-64 md:h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <div className="p-6 sm:p-8 flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <h2 className="text-2xl font-bold leading-tight">
                      {firstItem.title}
                    </h2>
                    {renderAdminButtons(firstItem)}
                  </div>

                  {!firstItem.published && isAdmin && (
                    <span className="mt-2 text-sm text-amber-600 font-medium">
                      (concept)
                    </span>
                  )}

                  {firstItem.body && (
                    <div className="mt-4 flex-grow">
                      <RichTextViewer
                        content={firstItem.body}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        );
      })()}

      {/* Rest of the items as draggable cards */}
      {filteredItems.length > 1 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredItems.slice(1).map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-6xl">
              {filteredItems.slice(1).map((item) => (
                <SortableNewsCard
                  key={item.id}
                  item={item}
                  isAdmin={isAdmin}
                  onOpen={openItem}
                  onEdit={openEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Nieuwsbericht bewerken' : 'Nieuw nieuwsbericht'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="news-title">Titel</Label>
              <Input
                id="news-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titel van het bericht"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="news-body">Inhoud</Label>
              <Textarea
                id="news-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Schrijf hier het bericht..."
                rows={6}
              />
            </div>
            <ContentImageField
              id="news-image"
              value={imageUrl}
              onChange={setImageUrl}
              label="Afbeelding"
            />
            <AudiencePicker
              roleScope={dialogRoleScope}
              scopeType={dialogScopeType}
              scopeId={dialogScopeId}
              hnhScopeType={dialogHnhScopeType}
              hnhScopeId={dialogHnhScopeId}
              onRoleScopeChange={setDialogRoleScope}
              onScopeChange={(type, id) => {
                setDialogScopeType(type);
                setDialogScopeId(id);
              }}
              onHnhScopeChange={(type, id) => {
                setDialogHnhScopeType(type);
                setDialogHnhScopeId(id);
              }}
            />

            {/* Topic selection */}
            <div className="grid gap-2">
              <Label>Topics</Label>
              <div className="flex flex-wrap gap-2">
                {newsTopics.map((topic) => {
                  const isSelected = dialogTopicIds.includes(topic.id);
                  return (
                    <Button
                      key={topic.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      className={`rounded-full text-xs ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300'
                          : 'border-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      onClick={() => {
                        setDialogTopicIds(
                          isSelected
                            ? dialogTopicIds.filter((id) => id !== topic.id)
                            : [...dialogTopicIds, topic.id],
                        );
                      }}
                    >
                      {topic.name}
                    </Button>
                  );
                })}
              </div>
              {/* Inline new topic creation */}
              <div className="flex gap-2 mt-1">
                <Input
                  value={inlineNewTopicName}
                  onChange={(e) => setInlineNewTopicName(e.target.value)}
                  placeholder="Nieuw topic..."
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!inlineNewTopicName.trim()) return;
                      const slug = inlineNewTopicName
                        .trim()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/(^-|-$)/g, '');
                      createTopic(inlineNewTopicName.trim(), slug)
                        .then(() => {
                          setInlineNewTopicName('');
                          toast({ title: 'Topic aangemaakt' });
                        })
                        .catch((err) =>
                          toast({ title: 'Fout', description: err.message, variant: 'destructive' }),
                        );
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 border-blue-400 text-blue-600 hover:bg-blue-50"
                  disabled={!inlineNewTopicName.trim()}
                  onClick={() => {
                    if (!inlineNewTopicName.trim()) return;
                    const slug = inlineNewTopicName
                      .trim()
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/(^-|-$)/g, '');
                    createTopic(inlineNewTopicName.trim(), slug)
                      .then(() => {
                        setInlineNewTopicName('');
                        toast({ title: 'Topic aangemaakt' });
                      })
                      .catch((err) =>
                        toast({ title: 'Fout', description: err.message, variant: 'destructive' }),
                      );
                  }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="news-published"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="news-published">Gepubliceerd</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleSave}
              disabled={submitting || !title.trim()}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {submitting ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Topic create/edit dialog (admin) */}
      <Dialog open={topicDialogOpen} onOpenChange={setTopicDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTopic ? 'Topic bewerken' : 'Nieuw topic'}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                if (editingTopic) {
                  await updateTopic(editingTopic.id, topicName, topicSlug);
                  toast({ title: 'Topic bijgewerkt' });
                } else {
                  await createTopic(topicName, topicSlug);
                  toast({ title: 'Topic aangemaakt' });
                }
                setTopicDialogOpen(false);
              } catch (err: any) {
                toast({ title: 'Fout', description: err.message, variant: 'destructive' });
              }
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="news-topic-name">Naam</Label>
              <Input
                id="news-topic-name"
                value={topicName}
                onChange={(e) => {
                  setTopicName(e.target.value);
                  if (!editingTopic) {
                    setTopicSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/(^-|-$)/g, ''),
                    );
                  }
                }}
                placeholder="bijv. Updates"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="news-topic-slug">Slug (URL)</Label>
              <Input
                id="news-topic-slug"
                value={topicSlug}
                onChange={(e) => setTopicSlug(e.target.value)}
                placeholder="bijv. updates"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setTopicDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingTopic ? 'Opslaan' : 'Aanmaken'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
